import React, { useEffect, useId, useRef } from 'react';
import Icon from './ui/Icon';

/**
 * Generic modal scaffold. Use it everywhere instead of inlining the
 * `modal-overlay` + `modal-content` + `e.stopPropagation()` triplet.
 *
 * Behavior:
 *   - Renders nothing while `open` is false (so callers can keep
 *     mounting the form unconditionally).
 *   - Click on the backdrop calls `onClose`. Click on the inner content
 *     does not bubble.
 *   - Pressing Escape calls `onClose`.
 *
 * ACCESSIBILITY — none of this was here, and a dialog without it is not a
 * dialog to anyone who is not looking at it:
 *   - `role="dialog"` + `aria-modal` so assistive tech treats the rest of the
 *     page as inert and announces the arrival rather than saying nothing.
 *   - `aria-labelledby` pointing at the rendered `title`, so the announcement
 *     names WHICH dialog. Falls back to `aria-label` when a caller renders its
 *     own header instead of passing `title`.
 *   - Focus moves into the dialog on open and RETURNS to whatever opened it on
 *     close. Without the return, dismissing a dialog dropped the caret back at
 *     the top of the document, so a keyboard user had to tab from the sidebar
 *     to get back to the button they just pressed.
 *   - Tab wraps inside the dialog. A dialog you can Tab out of, into the page
 *     behind a 60%-opacity scrim, is worse than no trap: focus is somewhere the
 *     user cannot see.
 *
 * By default the container receives focus, not the first field: it is
 * `tabIndex={-1}` so it is focusable programmatically but not in the Tab order,
 * the dialog's name is read on arrival, and nothing about which control a caller
 * happens to render first changes the behaviour. A field that asks for focus
 * with `autoFocus` keeps it — that is an explicit request, and overriding it
 * made `autoFocus` silently do nothing in every dialog.
 *
 * Props:
 *   - open:        boolean
 *   - onClose:     () => void   — backdrop click + Escape
 *   - title:       string?      — rendered as <h3> at the top, and used as the
 *                                  accessible name. Omit when the caller wants
 *                                  to render its own header (so it can include
 *                                  badges, close-x, etc.) — pass `label` then.
 *   - label:       string?      — accessible name when `title` is not rendered
 *   - maxWidth:    number?      — in px, defaults to 520
 *   - contentStyle: object?     — extra inline style merged into modal-content
 *                                 (e.g. `{ padding: 0 }` for full-bleed bodies)
 *   - description: node?        — one muted line under the title
 *   - showClose:   boolean      — corner ✕ button, default true (Escape and the
 *                                 backdrop still dismiss either way)
 *   - children:    body content. Put the buttons in a `.modal-footer`.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function Modal({ open, onClose, title, label, maxWidth = 520,
                                contentStyle, description, showClose = true, children }) {
  const contentRef = useRef(null);
  const returnFocusTo = useRef(null);
  const pressedOverlay = useRef(false);
  const wasOpen = useRef(false);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Remember the opener DURING RENDER, on the render that opens the dialog. By the
  // time any effect runs, a field with `autoFocus` has already taken focus inside
  // the dialog, so reading activeElement there records the field instead of the
  // button that opened it.
  if (open && !wasOpen.current) returnFocusTo.current = document.activeElement;
  wasOpen.current = open;

  // Take focus — unless a field inside already has it (autoFocus), which used to be
  // overridden here so that `autoFocus` never worked in a dialog — and hand it back
  // on close.
  useEffect(() => {
    if (!open) return undefined;
    if (!contentRef.current?.contains(document.activeElement)) contentRef.current?.focus();
    return () => {
      const el = returnFocusTo.current;
      // `isConnected` guards the common case where the opener was inside a row
      // the dialog's own action then removed from the DOM.
      if (el && el.isConnected && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  if (!open) return null;

  // Dismiss only when the press STARTED on the backdrop. Selecting text in a
  // field and releasing the mouse outside the dialog is a click on the overlay
  // too, and used to throw away a half-filled form.
  const onOverlayMouseDown = (e) => { pressedOverlay.current = e.target === e.currentTarget; };
  const onOverlayClick = (e) => {
    if (pressedOverlay.current && e.target === e.currentTarget) onClose?.();
    pressedOverlay.current = false;
  };

  // Tab wrap. Queried on each keypress rather than cached, because a dialog body
  // is usually a form whose set of enabled controls changes as it is filled in.
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(contentRef.current?.querySelectorAll(FOCUSABLE) || [])
      .filter((el) => el.offsetParent !== null);
    if (items.length === 0) {
      e.preventDefault();          // nothing to move to; stay put
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === contentRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={onOverlayMouseDown} onClick={onOverlayClick}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : label}
        tabIndex={-1}
        ref={contentRef}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth, ...contentStyle }}
      >
        {showClose && onClose && (
          <button type="button" className="btn btn-ghost btn-icon btn-sm modal-close"
                  onClick={onClose} aria-label="Close dialog">
            <Icon name="x" />
          </button>
        )}
        {title && <h3 id={titleId} className="modal-title">{title}</h3>}
        {description && <p className="text-muted" style={{ marginTop: -4, marginBottom: 'var(--sp-4)' }}>{description}</p>}
        {children}
      </div>
    </div>
  );
}
